import type { FormikErrors } from "formik";
import type {
  IEnrichedBusiness,
  PayPeriodTemplateCadence,
  PayPeriodTemplateStartDayOfWeek,
} from "@/business/business";
import {
  PAY_PERIOD_TEMPLATE_CADENCE,
  PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK,
} from "@/business/constants";
import {
  BUSINESS_TIME_ZONE_IANA,
  type BusinessTimeZoneLabel,
} from "@/business/time-zones";
import { dayjs, FORMAT_FILENAME_DATE } from "@/core/utils/date";

// The business time zones (`@/business/time-zones`) minus UTC. Admin deliberately
// excludes UTC too and reads it as "no time zone chosen yet", so offering it here would
// let an owner finish onboarding in a state Admin shows as unset.
export const BUSINESS_TIME_ZONES: readonly Exclude<
  BusinessTimeZoneLabel,
  "UTC"
>[] = (Object.keys(BUSINESS_TIME_ZONE_IANA) as BusinessTimeZoneLabel[]).filter(
  (label): label is Exclude<BusinessTimeZoneLabel, "UTC"> => label !== "UTC",
);

export const BUSINESS_TIME_ZONE_OPTIONS = BUSINESS_TIME_ZONES.map(
  (timeZone) => ({ value: timeZone, label: timeZone }),
);

export const PAY_PERIOD_CADENCE_LABEL: Record<
  PayPeriodTemplateCadence,
  string
> = {
  weekly: "Weekly",
  monthly: "Monthly",
  every_other_week: "Every other week",
  first_and_fifteenth: "1st and 15th",
};

export const START_DAY_OF_WEEK_LABEL: Record<
  PayPeriodTemplateStartDayOfWeek,
  string
> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

export const PAY_PERIOD_CADENCE_OPTIONS = Object.values(
  PAY_PERIOD_TEMPLATE_CADENCE,
).map((cadence) => ({
  value: cadence,
  label: PAY_PERIOD_CADENCE_LABEL[cadence],
}));

export const START_DAY_OF_WEEK_OPTIONS = Object.values(
  PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK,
).map((day) => ({
  value: day,
  label: START_DAY_OF_WEEK_LABEL[day],
}));

const START_DAY_NUMBER: Record<PayPeriodTemplateStartDayOfWeek, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

// The API speaks "YYYY-MM-DD"; the form and the date picker speak Date.
export const parseReferenceDate = (value: string | null | undefined) => {
  if (!value) return null;
  const parsed = dayjs(value, FORMAT_FILENAME_DATE, true);
  return parsed.isValid() ? parsed.toDate() : null;
};

export const isSelectableTimeZone = (timeZone: string | null | undefined) =>
  BUSINESS_TIME_ZONES.includes(
    timeZone as (typeof BUSINESS_TIME_ZONES)[number],
  );

export interface BusinessSetupFormValues {
  name: string;
  timeZone: string;
  serviceTitanTenantId: string;
  // Empty until the owner says. A business with no template of its own must not be shown
  // a cadence it never chose.
  cadence: PayPeriodTemplateCadence | "";
  startDayOfWeek: PayPeriodTemplateStartDayOfWeek | "";
  periodStartReferenceDate: Date | null;
}

type BusinessSetupSource = Pick<
  IEnrichedBusiness,
  "name" | "time_zone_label" | "current_pay_period_template"
> &
  Partial<Pick<IEnrichedBusiness, "service_titan_tenant_id">>;

export const getInitialBusinessSetupValues = (
  business: BusinessSetupSource,
): BusinessSetupFormValues => {
  const template = business.current_pay_period_template;
  const savedTemplate = Boolean(template?.id);

  return {
    name: business.name,
    // A business created before onboarding sits on UTC, which is not offered. Admin
    // blanks the same value rather than pre-selecting it, so the owner has to choose.
    timeZone: isSelectableTimeZone(business.time_zone_label)
      ? business.time_zone_label
      : "",
    serviceTitanTenantId: business.service_titan_tenant_id ?? "",
    // getEnrichedBusiness fabricates a weekly/Monday template for businesses that have
    // none, which is fine for display but would pre-fill this form with an answer the
    // owner never gave. Only a saved template has an id, so that is the tell.
    cadence: savedTemplate ? template.cadence : "",
    startDayOfWeek:
      savedTemplate && "start_day_of_week" in template
        ? template.start_day_of_week
        : "",
    periodStartReferenceDate:
      savedTemplate && "period_start_reference_date" in template
        ? parseReferenceDate(template.period_start_reference_date)
        : null,
  };
};

export const needsStartDay = (cadence: PayPeriodTemplateCadence | "") =>
  cadence === PAY_PERIOD_TEMPLATE_CADENCE.weekly ||
  cadence === PAY_PERIOD_TEMPLATE_CADENCE.every_other_week;

export const needsReferenceDate = (cadence: PayPeriodTemplateCadence | "") =>
  cadence === PAY_PERIOD_TEMPLATE_CADENCE.every_other_week;

// Split per step, so each screen only complains about what it asked for.
export const validateCompanyStep = (
  values: BusinessSetupFormValues,
): FormikErrors<BusinessSetupFormValues> => {
  const errors: FormikErrors<BusinessSetupFormValues> = {};

  if (!values.name.trim()) errors.name = "Company name is required.";
  if (!isSelectableTimeZone(values.timeZone)) {
    errors.timeZone = "Select a valid time zone.";
  }

  return errors;
};

export const validateScheduleStep = (
  values: BusinessSetupFormValues,
): FormikErrors<BusinessSetupFormValues> => {
  const errors: FormikErrors<BusinessSetupFormValues> = {};

  if (!values.cadence) errors.cadence = "Select how often you pay your team.";
  if (needsStartDay(values.cadence) && !values.startDayOfWeek) {
    errors.startDayOfWeek = "Select the first day of the pay period.";
  }
  if (needsReferenceDate(values.cadence)) {
    const referenceDate = values.periodStartReferenceDate;
    if (!referenceDate) {
      errors.periodStartReferenceDate = "Select a valid reference date.";
    } else if (
      !values.startDayOfWeek ||
      dayjs(referenceDate).day() !== START_DAY_NUMBER[values.startDayOfWeek]
    ) {
      errors.periodStartReferenceDate =
        "The reference date must match the selected start day.";
    }
  }

  return errors;
};

// Each step saves only its own fields, so a partly finished setup persists and going back
// to change one answer cannot blank another.
// Every flow has a ServiceTitan step now, and it is the only writer of anything
// ServiceTitan. The API refuses credentials sent from any other step.
export const buildCompanyPayload = (values: BusinessSetupFormValues) => ({
  name: values.name.trim(),
  time_zone_label: values.timeZone,
});

export const buildSchedulePayload = (values: BusinessSetupFormValues) => {
  // Validation blocks the submit until a cadence is chosen, so this only guards the type.
  if (!values.cadence) {
    throw new Error("A pay schedule is required before saving.");
  }

  return {
    current_pay_period_template_attributes: {
      cadence: values.cadence,
      ...(needsStartDay(values.cadence) && values.startDayOfWeek
        ? { start_day_of_week: values.startDayOfWeek }
        : {}),
      ...(needsReferenceDate(values.cadence) && values.periodStartReferenceDate
        ? {
            period_start_reference_date: dayjs(
              values.periodStartReferenceDate,
            ).format(FORMAT_FILENAME_DATE),
          }
        : {}),
    },
  };
};

export { PAY_PERIOD_TEMPLATE_CADENCE, PAY_PERIOD_TEMPLATE_START_DAY_OF_WEEK };
